// Colour maths for the style guide: hex to and from OKLCH, and WCAG contrast
// on white.

export type Rgb = [number, number, number]
export interface Oklch {
  l: number
  c: number
  h: number
}

export const parseHex = (value: string): Rgb | null => {
  const match = /^#([0-9a-f]{6})$/i.exec(value.trim())
  if (!match) return null
  const n = parseInt(match[1]!, 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

export const toHex = ([r, g, b]: Rgb) =>
  `#${[r, g, b].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`

const linear = (channel: number) => {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

const gamma = (c: number) => (c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055)

/** OKLCH (Björn Ottosson's Oklab, in polar form): lightness, chroma, hue. */
export const toOklch = ([r, g, b]: Rgb): Oklch => {
  const [lr, lg, lb] = [linear(r), linear(g), linear(b)]
  const l = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb)
  const m = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb)
  const s = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb)
  const L = 0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s
  const A = 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s
  const B = 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s
  const hue = (Math.atan2(B, A) * 180) / Math.PI
  return { l: L, c: Math.sqrt(A * A + B * B), h: hue < 0 ? hue + 360 : hue }
}

/** Linear sRGB for an OKLCH colour; channels outside 0–1 are out of gamut. */
const toLinear = ({ l: L, c, h }: Oklch): Rgb => {
  const A = c * Math.cos((h * Math.PI) / 180)
  const B = c * Math.sin((h * Math.PI) / 180)
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3
  return [
    4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  ]
}

const inGamut = (rgb: Rgb) => rgb.every((c) => c >= -1e-4 && c <= 1 + 1e-4)

/** sRGB for an OKLCH colour. Out of gamut, chroma is reduced until it fits,
 *  which keeps lightness and hue: a ramp stays even instead of clipping. */
export const fromOklch = (colour: Oklch): Rgb => {
  let { c } = colour
  if (!inGamut(toLinear(colour))) {
    let low = 0
    for (let i = 0; i < 20; i++) {
      const mid = (low + c) / 2
      if (inGamut(toLinear({ ...colour, c: mid }))) low = mid
      else c = mid
    }
    c = low
  }
  const rgb = toLinear({ ...colour, c })
  return rgb.map((channel) => Math.round(Math.min(1, Math.max(0, gamma(channel))) * 255)) as Rgb
}

/** WCAG 2 contrast ratio against white. */
export const contrastOnWhite = ([r, g, b]: Rgb) => {
  const luminance = 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b)
  return 1.05 / (luminance + 0.05)
}
